import type { Room, RoomMember } from 'matrix-js-sdk/src/matrix';

import { getRoomSortRecencyTimestamp } from 'modules/RoomsListItem/utils';
import { getIsFromGuestServer } from 'modules/Workspaces/WorkspacesProvider/utils';
import { isSystemUser, SHOW_MEEDIO_BOT_IN_UI } from 'utils/matrixUtils';

interface MemberScore {
  member: RoomMember;
  minRoomSize: number;
  numRooms: number;
  score: number;
}

// Only scan messages from the last hour
const EARLIEST_AGE_MS = 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;
const MAX_MESSAGES_PER_ROOM = 50;
const MAX_CONSIDERED_MEMBERS = 100;

// Stop processing rooms once we have this many candidate users (prevents heavy calc with 10k+ users)
export const MAX_SUGGESTED_USERS = 200;

// Recency order, computed once per room (Schwartzian transform). The shared sortRooms() re-derives
// the timestamp on every comparison and round-trips it through Date objects; here we read the
// timestamp once per room and sort on the raw number.
const sortRoomsByRecency = (rooms: Room[]): Room[] =>
  rooms
    .map((room) => ({ room, key: getRoomSortRecencyTimestamp(room) }))
    .sort((a, b) => b.key - a.key)
    .map((entry) => entry.room);

/**
 * Builds suggested users in a single pass over the recency-sorted rooms. Each room's joined-member
 * count and member list are read exactly once and feed three accumulators simultaneously:
 *   - recency: most recent message timestamp per sender within the last hour
 *   - connectivity: Adamic–Adar score over shared rooms (higher for many small shared rooms)
 *   - candidate cap: stop once MAX_SUGGESTED_USERS distinct candidates have been collected
 *
 * Excluded users and guest-server accounts are skipped as a hard rule — only registered users are
 * ever suggested. Final ranking is recency + connectivity, tie-broken by number of shared rooms.
 */
export const buildSuggestionsFromRooms = (rooms: Room[], excludedUserIds: string[]): RoomMember[] => {
  const excluded = new Set(excludedUserIds);
  const now = Date.now();
  const cutoffMs = now - EARLIEST_AGE_MS;

  const lastBySender = new Map<string, number>();
  const connectivity = new Map<string, MemberScore>();
  const candidates = new Set<string>();

  for (const room of sortRoomsByRecency(rooms)) {
    const memberCount = room.getJoinedMemberCount();
    if (memberCount >= MAX_CONSIDERED_MEMBERS) continue;

    // Recency: only the last N events, and only those within the time window.
    const events = room.getLiveTimeline().getEvents();
    for (let i = Math.max(0, events.length - MAX_MESSAGES_PER_ROOM); i < events.length; i++) {
      const event = events[i];
      const sender = event.getSender();
      const eventTs = event.getTs();
      if (!sender || eventTs <= cutoffMs) continue;

      const existing = lastBySender.get(sender);
      if (existing === undefined || eventTs > existing) lastBySender.set(sender, eventTs);
    }

    // Adamic–Adar weight for this room: smaller rooms contribute more per shared membership.
    const weight = 1 / Math.log(memberCount + 1);
    for (const member of room.getJoinedMembers()) {
      const { userId } = member;
      if (excluded.has(userId) || getIsFromGuestServer(userId)) continue;
      if (!SHOW_MEEDIO_BOT_IN_UI && isSystemUser(userId)) continue;

      candidates.add(userId);
      const entry = connectivity.get(userId);
      if (entry) {
        entry.score += weight;
        entry.numRooms += 1;
        if (memberCount < entry.minRoomSize) {
          entry.minRoomSize = memberCount;
          entry.member = member;
        }
      } else {
        connectivity.set(userId, { member, minRoomSize: memberCount, numRooms: 1, score: weight });
      }
    }

    if (candidates.size >= MAX_SUGGESTED_USERS) break;
  }

  const getTotalScore = (userId: string): number => {
    const lastSpoke = lastBySender.get(userId);
    const recency = lastSpoke === undefined ? 0 : 1 / (1 + (now - lastSpoke) / HOUR_MS);
    return recency + (connectivity.get(userId)?.score ?? 0);
  };

  return [...connectivity.values()]
    .sort((a, b) => {
      const scoreDiff = getTotalScore(b.member.userId) - getTotalScore(a.member.userId);
      if (scoreDiff !== 0) return scoreDiff;
      return b.numRooms - a.numRooms;
    })
    .map((entry) => entry.member)
    .slice(0, MAX_SUGGESTED_USERS);
};
